import { MagnifyingGlassIcon, UpdateIcon } from '@radix-ui/react-icons';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import api from '../../lib/api';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
    Dialog, DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from "../ui/dialog";
import { DropdownMenuItem } from '../ui/dropdown-menu';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';

function CheckCompetitionClassEligibilityDialog({ competitionClass }) {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [bunnies, setBunnies] = useState([]);
    const [hideEligible, setHideEligible] = useState(false);

    useEffect(() => {
        const fetchBunnies = async () => {
            if (competitionClass.id && isDialogOpen) {
                setIsLoading(true);
                try {
                    const response = await api.get(`https://skhrf-api.cmdigital.se/v1/registrations/${competitionClass.id}/eligibility`);
                    setBunnies(Array.isArray(response.data.bunnies) ? response.data.bunnies : []);
                } catch (error) {
                    setError(error);
                    setBunnies([]);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        if (isDialogOpen) {
            fetchBunnies();
        }
    }, [competitionClass.id, isDialogOpen]);

    // Filter based on the checkbox selection
    const filteredBunnies = hideEligible
        ? bunnies.filter(bunny => !bunny.allowed) // Only show non-eligible bunnies
        : bunnies;

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                <DialogTrigger className='flex items-center w-full text-left'>
                    <MagnifyingGlassIcon className='mr-3' /> Kontrollera klassbehörighet
                </DialogTrigger>
            </DropdownMenuItem>
            <DialogContent className="flex flex-col gap-6 max-h-dvh">
                <DialogHeader className='text-left'>
                    <DialogTitle>Kontrollera klassbehörighet</DialogTitle>
                    <DialogDescription>Se vilka kaniner som är behöriga att delta. Strukna kaniner visas ej.</DialogDescription>
                </DialogHeader>

                {/* Checkbox to hide/show eligible bunnies */}
                <div className="flex items-center">
                    <Label htmlFor="hideEligible" className='flex gap-2 items-center'>
                        <Checkbox
                            id="hideEligible"
                            checked={hideEligible}
                            onCheckedChange={(checked) => setHideEligible(checked)}
                        />
                        Visa endast obehöriga kaniner
                    </Label>
                </div>

                <ScrollArea className='h-[calc(70vh)] text-sm' type='always'>
                    {/* Display bunny list */}
                    {isLoading ? (
                        <UpdateIcon className='loader-spinner self-center size-6' />
                    ) : (
                        filteredBunnies.length > 0 ? (
                            filteredBunnies.map((bunny) => (
                                <div key={bunny.bunnyId} className="p-3 mt-3 border rounded-md">
                                    <div className='flex items-center gap-3'>
                                        <Link className='text-blue-600 font-medium hover:underline' to={`/bunny/${bunny.bunnyId}`}>
                                            {bunny.name}
                                        </Link>
                                        {bunny.allowed ?
                                            <Badge className='bg-success hover:bg-success/90'>Behörig</Badge>
                                            :
                                            <Badge variant='destructive'>Ej behörig</Badge>
                                        }
                                        {bunny.onlyCup && <Badge variant='outline'>Endast cup</Badge>}
                                    </div>
                                    {bunny.reason && <p className="text-red-500">{bunny.reason}</p>}
                                </div>
                            ))
                        ) : (
                            <p>Inga obehöriga kaniner att visa.</p>
                        )
                    )}
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
}

export default CheckCompetitionClassEligibilityDialog;
